import { Injectable, inject, signal, computed, NgZone, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, merge, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

export interface IdleTimeoutConfig {
  enabled: boolean;
  idleTimeMs: number;
  warningCountdownMs: number;
}

@Injectable({
  providedIn: 'root',
})
export class IdleTimeoutService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  // Configuration settings
  private readonly config: IdleTimeoutConfig = environment.idleTimeout || {
    enabled: true,
    idleTimeMs: 30 * 60 * 1000,     // 30 minutes
    warningCountdownMs: 60 * 1000,  // 60 seconds warning
  };

  // Signals for component reactivity
  readonly isWarningVisible = signal<boolean>(false);
  readonly remainingSeconds = signal<number>(Math.floor(this.config.warningCountdownMs / 1000));
  readonly totalWarningSeconds = signal<number>(Math.floor(this.config.warningCountdownMs / 1000));

  // Computed helper for formatted timer display (MM:SS)
  readonly formattedRemainingTime = computed(() => {
    const totalSecs = Math.max(0, this.remainingSeconds());
    const minutes = Math.floor(totalSecs / 60);
    const seconds = totalSecs % 60;
    const formattedMins = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const formattedSecs = seconds < 10 ? `0${seconds}` : `${seconds}`;
    return `${formattedMins}:${formattedSecs}`;
  });

  // Computed progress percentage for PrimeNG progress bar (100% -> 0%)
  readonly progressPercentage = computed(() => {
    const total = this.totalWarningSeconds();
    if (total <= 0) return 0;
    const current = Math.max(0, this.remainingSeconds());
    return Math.round((current / total) * 100);
  });

  private activitySubscription?: Subscription;
  private mainIdleTimer?: any;
  private countdownInterval?: any;
  private isMonitoring = false;

  /**
   * Starts monitoring user inactivity if authenticated and enabled in config.
   */
  startMonitoring(): void {
    if (!this.config.enabled || this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.setupEventListener();
    this.resetIdleTimer();
  }

  /**
   * Stops all event listeners and active timers.
   */
  stopMonitoring(): void {
    this.isMonitoring = false;
    this.clearTimers();
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = undefined;
    }
    this.isWarningVisible.set(false);
  }

  /**
   * Extends the session and resets the idle timer (e.g. when user clicks "Keep Me Logged In").
   */
  extendSession(): void {
    this.clearTimers();
    this.isWarningVisible.set(false);
    this.remainingSeconds.set(Math.floor(this.config.warningCountdownMs / 1000));
    if (this.isMonitoring) {
      this.resetIdleTimer();
    }
  }

  /**
   * Forcefully triggers immediate logout due to user inactivity.
   */
  logoutDueToInactivity(): void {
    this.stopMonitoring();
    this.authService.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'idle' } });
  }

  private setupEventListener(): void {
    // Run outside Angular zone to avoid unnecessary change detection cycles on mouse move
    this.ngZone.runOutsideAngular(() => {
      const mouseMove$ = fromEvent(window, 'mousemove');
      const keydown$ = fromEvent(window, 'keydown');
      const click$ = fromEvent(window, 'click');
      const scroll$ = fromEvent(window, 'scroll');
      const touch$ = fromEvent(window, 'touchstart');

      const userActivity$ = merge(mouseMove$, keydown$, click$, scroll$, touch$).pipe(
        throttleTime(1000)
      );

      this.activitySubscription = userActivity$.subscribe(() => {
        // Only reset idle timer if warning modal is not currently open
        if (this.isMonitoring && !this.isWarningVisible()) {
          this.resetIdleTimer();
        }
      });
    });
  }

  private resetIdleTimer(): void {
    this.clearTimers();
    
    // Time to wait before opening warning countdown modal
    const idlePeriodBeforeWarning = Math.max(0, this.config.idleTimeMs - this.config.warningCountdownMs);

    this.mainIdleTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.startWarningCountdown();
      });
    }, idlePeriodBeforeWarning);
  }

  private startWarningCountdown(): void {
    const warningSecs = Math.floor(this.config.warningCountdownMs / 1000);
    this.totalWarningSeconds.set(warningSecs);
    this.remainingSeconds.set(warningSecs);
    this.isWarningVisible.set(true);

    this.countdownInterval = setInterval(() => {
      this.ngZone.run(() => {
        const current = this.remainingSeconds() - 1;
        if (current <= 0) {
          this.remainingSeconds.set(0);
          this.logoutDueToInactivity();
        } else {
          this.remainingSeconds.set(current);
        }
      });
    }, 1000);
  }

  private clearTimers(): void {
    if (this.mainIdleTimer) {
      clearTimeout(this.mainIdleTimer);
      this.mainIdleTimer = undefined;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }
}
